import React from "react";
import { connect } from "react-redux";
import { WithStyles, withStyles } from "@material-ui/core/styles";
import CalendarIcon from "@material-ui/icons/DateRangeRounded";
import ChevronRight from "@material-ui/icons/ChevronRightRounded";
import Button from "@components/common/button/Button";
import ImgAvatar from "@components/common/avatar/ImgAvatar";
import { IFamilyMember } from "@app-types/websocket/family.types";
import { IExternalCalendar } from "@app-types/websocket/externalCalender.types";
import { externalCalendarStyles } from "./externalCalender.style";
import { calendarSource, calendarViewers } from "./externalCalendar.utils";
import TranslateNew from "@translate/TranslateNew";
import { getSelectedFamilyResource } from "@services/user/user.utils";

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof externalCalendarStyles> {
  externalCalendars: IExternalCalendar[];
  family: IFamilyMember[];
  userId: number;
  onCalendarSelect?: (calendarId: number) => void;
}

export const ExternalCalendarList: React.FC<IProps> = ({ externalCalendars, family, userId, classes, onCalendarSelect }) => {
  const person = (id: number) => {
    const member = family.find((m) => m.id === id);
    return <span key={id} className={classes.person}>
      {member && <ImgAvatar imgName={member.avatar} color={member.color}
        imgUrl={member.imageAvatar ? member.avatarURL : undefined} style={{ width: 24, height: 24 }} />}
      {id === userId ? tn.lang.externalCalYou : member ? member.firstName : tn.lang.externalCalMember}
    </span>;
  };

  if (!externalCalendars.length) {
    return <div className={classes.empty}>
      <CalendarIcon />
      <h3 className={classes.emptyTitle}>{tn.lang.externalCalEmptyTitle}</h3>
      <p className={classes.description}>{tn.lang.externalCalEmptyDescription}</p>
    </div>;
  }

  return <div className={classes.list}>
    {externalCalendars.map((calendar) => {
      const owned = calendar.creator === userId;
      const creator = family.find((m) => m.id === calendar.creator);
      const viewers = calendarViewers(calendar);
      const source = calendarSource(calendar.url);
      return <article key={calendar.id} className={classes.card} aria-labelledby={`calendar-${calendar.id}-title`}>
        <div className={classes.cardHeader}>
          <div className={classes.calendarIcon}><CalendarIcon /></div>
          <div className={classes.heading}>
            <h3 id={`calendar-${calendar.id}-title`} className={classes.cardTitle}>{calendar.calendarName}</h3>
            <p className={classes.source}>{tn.lang.externalCalSource}: {source || tn.lang.externalCalStepLink}</p>
          </div>
        </div>
        <dl className={classes.metadata}>
          <dt className={classes.metaLabel}>{tn.lang.externalCalPerson}</dt>
          <dd className={classes.metaValue}>
            {calendar.calendarPerson ? person(calendar.calendarPerson) : tn.lang.externalCalNoPerson}
          </dd>
          <dt className={classes.metaLabel}>{tn.lang.externalCalVisibleTo}</dt>
          <dd className={classes.metaValue}>
            {owned && viewers.every((id) => id === userId) ? tn.lang.externalCalOnlyYou : viewers.map(person)}
          </dd>
        </dl>
        <div className={classes.cardFooter}>
          <span className={classes.ownership}>
            {owned ? tn.lang.externalCalMine : tn.lang.externalCalSharedBy(creator ? creator.firstName : tn.lang.externalCalMember)}
          </span>
          <Button className={classes.textButton} endIcon={<ChevronRight />}
            aria-label={`${owned ? tn.lang.externalCalManage : tn.lang.externalCalDetails}: ${calendar.calendarName}`}
            onClick={() => calendar.id !== undefined && onCalendarSelect && onCalendarSelect(calendar.id)}>
            {owned ? tn.lang.externalCalManage : tn.lang.externalCalDetails}
          </Button>
        </div>
      </article>;
    })}
  </div>;
};

const mapStateToProps = (state: any) => ({
  externalCalendars: (state.externalCalendar.externalCalendars || []).filter(
    (cal: IExternalCalendar) => cal.familyId === getSelectedFamilyResource(state.user?.session)?.id),
  family: state.family.family || [],
  userId: state.user.session.data.id,
});

export default connect(mapStateToProps)(withStyles(externalCalendarStyles)(ExternalCalendarList));



// WEBPACK FOOTER //
// ./src/components/calendar2/externalCalendar/ExternalCalendarList.tsx